import { createCrudProp } from '../utils/crud-prop'
import { replaceObjectRawKeys } from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { sortByTimeIfExists } from '../utils/sort-by'
import { eventSelectors } from './events'

const drugsAdminProp = createCrudProp({
  rootKey: 'drugsAdministered',
  idPrefix: 'dose',
  requiredProps: ['route'],
  sortFn: sortByTimeIfExists,
  mapFn: replaceObjectRawKeys,
})

/**
 * @param {{
 *   drugVariantId: string
 *   amount_raw: string
 *   unit: LocalAnestheticUnit
 *   epiConcentration?: number
 *   time?: number
 * }} localDoseArgs
 *   Local dose details
 * @returns {any} The changes to apply to the case.
 */
export const actionAddLocalDose = ({
  drugVariantId,
  amount_raw,
  epiConcentration,
  unit,
  time,
}) =>
  drugsAdminProp.actionAdd({
    route: 'local',
    drugVariantId,
    amount_raw,
    unit: unit || 'mg',
    epiConcentration: epiConcentration || 0,
    time: time || Date.now(),
    shouldStartCase: false,
  })

/**
 * @param {{
 *   drugId: string
 *   amount_raw: string
 *   route: ParenDrugRoute
 *   doseId?: string
 *   time?: number
 * }} parenteralDoseArgs
 *   Parenteral dose details
 * @returns {any} The changes to apply to the case.
 */
export const actionAddParenteralDose = ({
  drugId,
  amount_raw,
  route,
  doseId,
  time,
}) =>
  drugsAdminProp.actionAdd(
    {
      route,
      drugId,
      amount_raw,
      time: time || Date.now(),
      shouldStartCase: true,
    },
    doseId
  )

/**
 * @param {{
 *   drugId: string
 *   amount_raw?: string | null
 *   route: OralSedativeRoute
 *   shouldStartCase: boolean
 *   editing?: boolean
 *   time?: number | null
 * }} oralDoseArgs
 *   Oral dose details
 * @returns {any} The changes to apply to the case.
 */
export const actionAddOralSedativeDose = ({
  drugId,
  amount_raw,
  route,
  shouldStartCase,
  editing,
  time,
}) => {
  const args = {
    drugId,
    route,
    editing: editing || false,
    shouldStartCase,
  }
  // allow passing explicit null to not set at all
  // this is used by presets for pre-procedure sedatives
  if (amount_raw !== null) {
    args.amount_raw = amount_raw || '0'
  }
  // allow passing explicit null to not set at all
  // this is used by presets for pre-procedure sedatives
  if (time !== null) {
    args.time = time || Date.now()
  }
  return drugsAdminProp.actionAdd(args)
}

/**
 * @param {{
 *   drugId: string
 *   amount: number
 *   shouldStartCase: boolean
 *   time?: number
 * }} inhalationDoseArgs
 *   Inhalation dose details
 * @returns {any} The changes to apply to the case.
 */
export const actionAddInhalationDose = ({
  drugId,
  amount,
  shouldStartCase,
  time,
}) =>
  drugsAdminProp.actionAdd({
    drugId,
    route: 'inhalation',
    amount,
    time: time || Date.now(),
    shouldStartCase,
  })

/**
 * @param {{
 *   drugId: string
 *   amount_raw: string
 *   route: InfusionRoute
 *   time?: number
 *   shouldStartCase?: boolean
 * }} infusionDoseArgs
 *   Infusion dose details
 * @returns {any} The changes to apply to the case.
 */
export const actionAddInfusionDose = ({
  drugId,
  amount_raw,
  route,
  time,
  shouldStartCase,
}) =>
  drugsAdminProp.actionAdd({
    drugId,
    amount_raw,
    route,
    time: time || Date.now(),
    shouldStartCase: shouldStartCase !== false,
  })

/**
 * @param {{
 *   siteId: string
 *   bagId: string
 *   solutionType: string
 *   amount_raw: string
 *   route: FluidRoute
 *   time?: number
 * }} opts
 * @returns {any} The changes to apply to the case.
 */
export const actionAddFluidDose = ({
  solutionType,
  siteId,
  bagId,
  amount_raw,
  route,
  time,
}) =>
  drugsAdminProp.actionAdd({
    drugId: solutionType,
    siteId,
    route,
    amount_raw,
    bagId,
    time: time || Date.now(),
  })

/**
 * @param {{
 *   siteId: string
 *   solutionType: string
 *   amount_raw: string
 *   time?: number
 * }} opts
 * @returns {any} The changes to apply to the case.
 */
export const actionAddSyringeFlush = ({
  solutionType,
  siteId,
  amount_raw,
  time,
}) =>
  drugsAdminProp.actionAdd({
    drugId: solutionType,
    siteId,
    route: 'syringeFlush',
    amount_raw,
    time: time || Date.now(),
  })

/**
 * @param {string} id The airway ID
 * @returns {any} The changes to apply to the case.
 */
export const actionRemoveDose = drugsAdminProp.actionRemove

/**
 * @param {string} id The airway ID
 * @param {any} updates The updates to apply to the airway
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateDose = drugsAdminProp.actionUpdate

/** @type {CaseSelector<DoseAdministered[]>} */
export const selectAllDosesAdministered = drugsAdminProp.select

/** @type {CaseSelector<CaseData['drugsAdministered']>} */
export const selectAllDosesAdministeredRaw = drugsAdminProp.selectRaw

const getFilteredDoseSelector = filterFn =>
  createSelector(selectAllDosesAdministered, doses => doses.filter(filterFn))

const getByIdSelector = (baseSelector, prop = 'drugId') =>
  createSelector(baseSelector, doses =>
    doses.reduce((res, dose) => {
      if (!res[dose[prop]]) {
        res[dose[prop]] = []
      }
      res[dose[prop]].push(dose)
      return res
    }, {})
  )

/** @type {CaseSelector<LocalDoseAdministeredRaw[]>} */
export const selectLocalDosesAdministeredRaw = getFilteredDoseSelector(
  dose => dose.route === 'local'
)

/** @type {CaseSelector<{ [drugId: string]: LocalDoseAdministeredRaw[] }>} */
export const selectLocalDosesAdministeredRawById = getByIdSelector(
  selectLocalDosesAdministeredRaw,
  'drugVariantId'
)

const addMeta = selector => caseData => {
  const caseStart = eventSelectors.selectCaseStartTime(caseData)
  return selector(caseData).map(dose => ({
    ...dose,
    isPreProcedure: caseStart ? dose.time < caseStart : true,
    isComplete: !!(dose.amount && dose.route && dose.time),
  }))
}

/** @type {CaseSelector<ParenDoseAdministered[]>} */
export const selectParenteralDosesAdministered = addMeta(
  getFilteredDoseSelector(
    dose => dose.route === 'IV' || dose.route === 'IM' || dose.route === 'IN'
  )
)

/** @type {CaseSelector<{ [drugId: string]: ParenDoseAdministered[] }>} */
export const selectParenteralDosesAdministeredByDrugId = getByIdSelector(
  selectParenteralDosesAdministered
)

/** @type {CaseSelector<OralDoseAdministered[]>} */
export const selectOralSedativeDosesAdministered = addMeta(
  getFilteredDoseSelector(dose => dose.route === 'PO' || dose.route === 'SL')
)

/** @type {CaseSelector<{ [drugId: string]: OralDoseAdministered[] }>} */
export const selectOralSedativeDosesAdministeredByDrugId = getByIdSelector(
  selectOralSedativeDosesAdministered
)

/** @type {CaseSelector<({ id: string } & InhalationDoseAdministered)[]>} */
export const selectInhalationDosesAdministered = getFilteredDoseSelector(
  dose => dose.route === 'inhalation'
)

/** @type {CaseSelector<{ [drugId: string]: InhalationDoseAdministered[] }>} */
export const selectInhalationDosesAdministeredByDrugId = getByIdSelector(
  selectInhalationDosesAdministered
)

/** @type {CaseSelector<({ id: string } & InfusionDoseAdministered)[]>} */
export const selectInfusionDosesAdministered = getFilteredDoseSelector(
  dose => dose.route === 'infusion' || dose.route === 'infusionBolus'
)

/** @type {CaseSelector<{ [drugId: string]: InfusionDoseAdministered[] }>} */
export const selectInfusionDosesAdministeredByDrugId = getByIdSelector(
  selectInfusionDosesAdministered
)

/** @type {CaseSelector<({ id: string } & FluidDoseAdministered)[]>} */
export const selectFluidDosesAdministered = getFilteredDoseSelector(
  dose =>
    dose.route === 'fluid' ||
    dose.route === 'fluidBolus' ||
    dose.route === 'syringeFlush'
)

/** @type {CaseSelector<({ id: string } & FluidDoseAdministered)[]>} */
export const selectSyringeFlushesBySiteId = getByIdSelector(
  getFilteredDoseSelector(dose => dose.route === 'syringeFlush'),
  'siteId'
)

/** @type {CaseSelector<boolean>} */
export const selectHasFluidDoseDetail = createSelector(
  selectFluidDosesAdministered,
  doses => doses.some(dose => dose.amount)
)

/** @type {CaseSelector<{ [drugId: string]: FluidDoseAdministered[] }>} */
export const selectFluidDosesAdministeredBySiteId = getByIdSelector(
  selectFluidDosesAdministered,
  'siteId'
)

/** @type {CaseSelector<PreProcedureSedativeDose[]>} */
export const selectPreProcedureSedativeDosesAdministered = createSelector(
  addMeta(
    getFilteredDoseSelector(
      dose =>
        dose.route === 'PO' ||
        dose.route === 'SL' ||
        dose.route === 'IM' ||
        dose.route === 'IN'
    )
  ),
  /** @param {(OralDoseAdministered | ParenDoseAdministered)[]} doses */
  doses => doses.filter(dose => dose.isPreProcedure)
)
